import { addDays, subDays, subYears } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { AmenityListItem } from '@/api/reservations/get-amenities'
import {
  ReservationDashboardFilterCounts,
  ReservationDashboardRow,
  ReservationStatus,
} from '@/api/reservations/get-reservation-dashboard'
import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { ApproveDeclineButtons } from '@/components/ui/approve-decline-buttons'
import { Badge, BadgeColorKeys } from '@/components/ui/badge'
import { PeriodOption } from '@/components/ui/date-picker'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { QuantityBadgeColor } from '@/components/ui/quantity-badge/quantity-badge.types'
import { SelectTreeOption } from '@/components/ui/select'
import { StatusTabOption } from '@/components/ui/status-tabs'
import { TableCell, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { UserRoleEnum } from '@/models/features/user.models'
import { DateRange } from '@/models/helpers'
import { formatDollars } from '@/utils/currency'
// Note: startTime/endTime are "minutes since midnight" in building-local time, not epoch
// timestamps - formatMinutes formats them directly, no timezone conversion needed.
import { formatDateOnly, formatMinutes, formatTimeOnly } from '@/utils/date'

import { getBuildingToday, getDefaultDateRange } from '../reservations-dates.utils'

export const RESERVATIONS_TABLE_ID = 'reservations-dashboard-table'

const DEFAULT_DAYS_RANGE = 7

// Note: shared default date-range window (7 days including today, same as the "Next 7 days"
// preset) for both the Reservations and Time Blocks tabs.
export const getDefaultRange = (timezone: string): [number, number] =>
  getDefaultDateRange(timezone, DEFAULT_DAYS_RANGE)

const DATE_RANGE_FILTER_KEYS = ['allDates', 'fromDate', 'toDate']

// Note: TableGroup's own "Clear filters" button keeps the date keys (they're base filters), but
// per spec it must return the tab to the default range too - wrap the reset so it also resets them.
export const withDateRangeReset = <
  T extends { handleResetFilters: (props?: { resetKeys?: string[] }) => void },
>(
  tableFilters: T,
): T => ({
  ...tableFilters,
  handleResetFilters: (props) =>
    tableFilters.handleResetFilters({
      ...props,
      resetKeys: [...(props?.resetKeys ?? []), ...DATE_RANGE_FILTER_KEYS],
    }),
})

// Note: the shared DatePicker's default "Period" list is Today/Yesterday/Last 7/14/30
// days - fine for the rest of the app, but these tabs need future ranges too (reservations
// can be booked ahead) plus longer lookbacks, per spec. "Next N days" mirrors the existing
// "Last N days" convention (N calendar days, inclusive of today) rather than introducing a
// different span rule.
const NEXT_DAYS_PRESETS = [7, 30, 60, 90, 120]
const LAST_DAYS_PRESETS = [7, 14, 30, 60, 90, 120]
const LAST_YEARS_PRESETS = [1, 2, 3, 5]

const getTodayOption = (t: TFunction<['reservations']>, timezone: string): PeriodOption => ({
  getValue: (): DateRange => {
    const today = getBuildingToday(timezone)
    return [today, today]
  },
  label: t('reservations:shared.periodOptions.today'),
})

const getYesterdayOption = (t: TFunction<['reservations']>, timezone: string): PeriodOption => ({
  getValue: (): DateRange => {
    const yesterday = subDays(getBuildingToday(timezone), 1)
    return [yesterday, yesterday]
  },
  label: t('reservations:shared.periodOptions.yesterday'),
})

const getNextDaysOptions = (t: TFunction<['reservations']>, timezone: string): PeriodOption[] =>
  NEXT_DAYS_PRESETS.map((days) => ({
    getValue: (): DateRange => {
      const today = getBuildingToday(timezone)
      return [today, addDays(today, days - 1)]
    },
    label: t('reservations:shared.periodOptions.nextDays', { days }),
  }))

const getLastDaysOptions = (
  t: TFunction<['reservations']>,
  timezone: string,
  presets: number[] = LAST_DAYS_PRESETS,
): PeriodOption[] =>
  presets.map((days) => ({
    getValue: (): DateRange => {
      const today = getBuildingToday(timezone)
      return [subDays(today, days - 1), today]
    },
    label: t('reservations:shared.periodOptions.lastDays', { days }),
  }))

const getLastYearsOptions = (t: TFunction<['reservations']>, timezone: string): PeriodOption[] =>
  LAST_YEARS_PRESETS.map((years) => ({
    getValue: (): DateRange => {
      const today = getBuildingToday(timezone)
      return [subYears(today, years), today]
    },
    label:
      years === 1
        ? t('reservations:shared.periodOptions.lastYear')
        : t('reservations:shared.periodOptions.lastYears', { years }),
  }))

export const getReservationsPeriodOptions = (
  t: TFunction<['reservations']>,
  timezone: string,
): PeriodOption[] => [
  getTodayOption(t, timezone),
  ...getNextDaysOptions(t, timezone),
  getYesterdayOption(t, timezone),
  ...getLastDaysOptions(t, timezone),
  ...getLastYearsOptions(t, timezone),
]

// Note: Analytics only reports on completed activity (no future bookings to look ahead
// to) and, per spec, offers a narrower lookback than the table's own date filter - just
// Today/Yesterday/Last 7/14/30 days, no 60/90/120-day or year presets.
const ANALYTICS_LAST_DAYS_PRESETS = [7, 14, 30]

export const getReservationsAnalyticsPeriodOptions = (
  t: TFunction<['reservations']>,
  timezone: string,
): PeriodOption[] => [
  getTodayOption(t, timezone),
  getYesterdayOption(t, timezone),
  ...getLastDaysOptions(t, timezone, ANALYTICS_LAST_DAYS_PRESETS),
]

export const getAmenitySpacePlaceholder = (t: TFunction<['reservations']>): string =>
  t('reservations:shared.amenitySpaceFilter.placeholder')

// Note: the dropdown's own search input swaps its placeholder to indicate a
// selection is active, once anything is checked.
export const getAmenitySpaceSearchPlaceholder = (
  t: TFunction<['reservations']>,
  selectedCount: number,
): string =>
  selectedCount > 0
    ? t('reservations:shared.amenitySpaceFilter.searchPlaceholderSelected')
    : t('reservations:shared.amenitySpaceFilter.searchPlaceholder')

// Note: statuses shown on the "Reservations" tab - "blocked" rows live on the "Time blocks" tab.
export const NON_BLOCKED_STATUSES: ReservationStatus[] = [
  'pending',
  'approved',
  'ongoing',
  'completed',
  'cancelled',
]

export const ReservationStatusFilterEnum = z.enum(['All', ...NON_BLOCKED_STATUSES])
export type ReservationStatusFilter = z.infer<typeof ReservationStatusFilterEnum>

// Note: shared between the Cancel/Decline reservation confirmation popups.
export const RefundStateEnum = z.enum(['refund', 'no_refund', 'partial'])
export type RefundState = z.infer<typeof RefundStateEnum>

export const reservationsTableFiltersSchema = z.object({
  // Note: `allDates` = the date range was cleared (no date filter). Absent from the URL means
  // the default range applies, so old links and links without it keep working.
  allDates: z.stringbool().optional(),
  // Note: raw checkbox-tree selection ("a-<amenityId>" / "l-<locationId>") - see
  // splitAmenitySpaceSelection. One filter key for one filter control.
  amenitiesAndSpaces: z.array(z.string()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  status: ReservationStatusFilterEnum.optional(),
  toDate: z.coerce.number().optional(),
})

export type ReservationsTableFilters = z.infer<typeof reservationsTableFiltersSchema>

// Note: default sort - future -> past by start date/time, per spec.
export const reservationsTableInitialFilters: ReservationsTableFilters = {
  allDates: false,
  amenitiesAndSpaces: [],
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: 'date',
  sortOrder: 'desc',
  status: ReservationStatusFilterEnum.enum.All,
  toDate: undefined,
}

export const STATUS_COLOR_MAP: Record<ReservationStatus, BadgeColorKeys> = {
  approved: 'green',
  cancellation_requested: 'red',
  cancelled: 'red',
  completed: 'neutral',
  ongoing: 'blue',
  pending: 'orange',
}

export const getStatusLabel = (
  t: TFunction<['reservations']>,
  status: ReservationStatus,
): string => {
  const labels: Record<ReservationStatus, string> = {
    approved: t('reservations:dashboard.status.approved'),
    cancellation_requested: t('reservations:dashboard.status.cancellationRequested'),
    cancelled: t('reservations:dashboard.status.cancelled'),
    completed: t('reservations:dashboard.status.completed'),
    ongoing: t('reservations:dashboard.status.ongoing'),
    pending: t('reservations:dashboard.status.pending'),
  }
  return labels[status]
}

export const getReservationStatusTabOptions = (
  t: TFunction<['reservations']>,
  counts: Partial<Record<'count' | ReservationStatus, number>>,
): StatusTabOption<ReservationStatusFilter>[] => [
  { count: counts.count ?? 0, label: t('reservations:dashboard.status.all'), value: 'All' },
  ...NON_BLOCKED_STATUSES.map((status) => ({
    // Note: the Pending count badge is highlighted yellow, distinct from its (orange) row
    // status pill color in STATUS_COLOR_MAP - deliberately not the same value everywhere.
    color: (status === 'pending' ? 'yellow' : STATUS_COLOR_MAP[status]) as QuantityBadgeColor,
    count: counts[status] ?? 0,
    label: getStatusLabel(t, status),
    value: status,
  })),
]

// TODO: Time Blocks still tallies its counts client-side through this unpaginated query
// (`/reservation/dashboard/time-block` has no `filterCounts` yet) - drop once the BE adds it.
export const AMENITY_SPACE_COUNTS_QUERY_LIMIT = 1_000_000

export type AmenitySpaceCounts = Record<string, number>

export const toAmenitySpaceCounts = (
  filterCounts?: ReservationDashboardFilterCounts,
): AmenitySpaceCounts => {
  const counts: AmenitySpaceCounts = {}

  for (const [id, count] of Object.entries(filterCounts?.amenities ?? {})) {
    counts[`a-${id}`] = count
  }
  for (const [id, count] of Object.entries(filterCounts?.locations ?? {})) {
    counts[`l-${id}`] = count
  }

  return counts
}

// Note: values are namespaced ("a-"/"l-") because amenity ids and location ids are
// separate DB sequences - an unprefixed merge could collide (amenity 5 vs space 5).
// An amenity with spaces is a pure container (its own value is never selectable -
// checking it selects all its spaces, per the tree-checkbox component's model); an
// amenity with none is itself a leaf, selectable directly. `counts`, when provided,
// attaches a trailing quantity-badge count to each row - omit it (e.g. Time Blocks'
// reuse of this same tree) to render plain, count-less rows.
export const buildAmenitySpaceTreeOptions = (
  amenities: AmenityListItem[],
  counts?: AmenitySpaceCounts,
): SelectTreeOption<string>[] => {
  const countFor = (value: string) => (counts ? (counts[value] ?? 0) : undefined)

  return amenities.map((amenity) => {
    const amenityValue = `a-${amenity.id}`

    if (amenity.locations.length === 0) {
      return { count: countFor(amenityValue), label: amenity.name, value: amenityValue }
    }

    const children = amenity.locations.map((location) => {
      const locationValue = `l-${location.id}`
      return { count: countFor(locationValue), label: location.name, value: locationValue }
    })

    return {
      children,
      count: countFor(amenityValue),
      label: amenity.name,
      value: amenityValue,
    }
  })
}

export const splitAmenitySpaceSelection = (
  selection: string[],
): { amenityIds: number[]; locationIds: number[] } => {
  const amenityIds: number[] = []
  const locationIds: number[] = []

  for (const value of selection) {
    if (value.startsWith('a-')) amenityIds.push(Number(value.slice(2)))
    else if (value.startsWith('l-')) locationIds.push(Number(value.slice(2)))
  }

  return { amenityIds, locationIds }
}

interface ReservationsActionsColumnOptions {
  approvingIds: Set<number>
  onApprove: (row: ReservationDashboardRow) => void
  onDecline: (row: ReservationDashboardRow) => void
}

interface ReservationsPersonColumnSettings {
  reservedForColumnSettings: PersonColumnSettings
  createdByColumnSettings: PersonColumnSettings
  approvedByColumnSettings: PersonColumnSettings
}

const formatReservationTimestamp = (timestamp: number, timezone: string) =>
  `${formatDateOnly(toZonedTime(timestamp, timezone))}, ${formatTimeOnly(toZonedTime(timestamp, timezone))}`

export const getReservationsTableColumns = (
  t: TFunction<['reservations']>,
  timezone: string,
  { approvingIds, onApprove, onDecline }: ReservationsActionsColumnOptions,
  {
    reservedForColumnSettings,
    createdByColumnSettings,
    approvedByColumnSettings,
  }: ReservationsPersonColumnSettings,
): TableColumn<ReservationDashboardRow>[] => [
  // Note: the "Reserved For" person column of EL-15064 (BE key Reservations → reservedFor, Mixed).
  // A booking is either a resident's or a guest's - the BE says which (reservedForRole) and fills
  // the guest's name, email and phone; a guest has no avatar, unit, building number or address.
  getPersonColumn<ReservationDashboardRow>(reservedForColumnSettings, {
    getDisplay: (row) => row.reservedForDisplay,
    getPerson: (row) => {
      if (!row.reservedForRole) return null
      const isGuest = row.reservedForRole === 'Guest'
      return {
        apartment: row.reservedForUnit,
        avatar: row.reservedForAvatar,
        buildingNumber: row.reservedForBuildingNumber,
        clientId: isGuest ? null : row.client,
        email: row.reservedForEmail,
        firstName: row.reservedForFirstName,
        kind: isGuest ? 'guest' : 'resident',
        lastName: row.reservedForLastName,
        phone: row.reservedForPhone,
        street: row.reservedForStreet,
      }
    },
    id: 'reservedFor',
    pageName: t('reservations:dashboard.pageName'),
    renderExtra: (row) =>
      row.source === 'Marketplace' && (
        <Badge color="green" size="small">
          {t('reservations:dashboard.badges.amenityMarketplace')}
        </Badge>
      ),
    source: 'reservations',
    title: t('reservations:dashboard.columns.reservedFor'),
  }),
  {
    id: 'amenity',
    render: (row) => (
      <div>
        <TableCell breakLines={2} color="black800">
          {row.amenityName}
        </TableCell>
        {row.locationName && (
          <Typography color="black500" size="text-small">
            {row.locationName}
          </Typography>
        )}
      </div>
    ),
    title: t('reservations:dashboard.columns.amenity'),
    width: '18rem',
  },
  {
    id: 'start',
    render: (row) =>
      row.date ? (
        <div>
          <Typography color="black800" size="text-small">
            {formatDateOnly(toZonedTime(row.date, timezone))}
          </Typography>
          {!row.endDate && (
            <Typography color="black800" size="text-small">
              {formatMinutes(row.startTime, { padHours: false })}
            </Typography>
          )}
        </div>
      ) : (
        EMPTY_VALUE
      ),
    sortAscLabel: t('reservations:dashboard.sort.oldestFirst'),
    sortDescLabel: t('reservations:dashboard.sort.newestFirst'),
    sortKey: 'date',
    title: t('reservations:dashboard.columns.start'),
    width: '11rem',
  },
  {
    id: 'end',
    render: (row) => {
      const endDate = row.endDate ?? row.date
      if (!endDate) return EMPTY_VALUE

      // endTime 1440 means midnight at the end of the day, so it belongs to the next day.
      // Multi-day rows (endDate set) show no time, so their date stays as is.
      const MINUTES_IN_DAY = 24 * 60
      const daysToAdd = row.endDate ? 0 : Math.floor((row.endTime ?? 0) / MINUTES_IN_DAY)

      return (
        <div>
          <Typography color="black800" size="text-small">
            {formatDateOnly(addDays(toZonedTime(endDate, timezone), daysToAdd))}
          </Typography>
          {!row.endDate && (
            <Typography color="black800" size="text-small">
              {formatMinutes(row.endTime, { padHours: false })}
            </Typography>
          )}
        </div>
      )
    },
    sortAscLabel: t('reservations:dashboard.sort.oldestFirst'),
    sortDescLabel: t('reservations:dashboard.sort.newestFirst'),
    sortKey: 'endDate',
    title: t('reservations:dashboard.columns.end'),
    width: '11rem',
  },

  {
    id: 'netPaid',
    render: (row) => (
      <Typography color="black800" size="text-small">
        {formatDollars(row.netPaid, { zeroPrice: true })}
      </Typography>
    ),
    sortKey: 'netPaid',
    title: t('reservations:dashboard.columns.netPaid'),
    width: '8rem',
  },
  // Note: Reservations → createdBy (Mixed on the BE) - a staff member, or the resident when they
  // booked in the app (role "Client"; only then is there a unit / building / address / profile).
  // The created-at time keeps leading the second line, like Packages' check-in / check-out time.
  getPersonColumn<ReservationDashboardRow>(createdByColumnSettings, {
    getDisplay: (row) => row.createdByDisplay,
    getLeadingDetail: (row) => formatReservationTimestamp(row.createdAt, timezone),
    getPerson: (row) => {
      if (!row.createdByUser) return null
      const isResident = row.createdByRole === UserRoleEnum.enum.Client
      return {
        apartment: isResident ? row.createdByUnit : null,
        avatar: row.createdByAvatar,
        buildingNumber: isResident ? row.createdByBuildingNumber : null,
        clientId: isResident ? row.createdByClientId : null,
        email: row.createdByEmail,
        firstName: row.createdByUser.firstName,
        kind: isResident ? 'resident' : 'staff',
        lastName: row.createdByUser.lastName,
        role: row.createdByRole,
        street: isResident ? row.createdByStreet : null,
      }
    },
    id: 'createdBy',
    pageName: t('reservations:dashboard.pageName'),
    sortKey: 'createdBy',
    source: 'reservations',
    title: t('reservations:dashboard.columns.createdBy'),
  }),
  // Note: Reservations → approvedBy (Staff on the BE). The approval time leads the second line; an
  // approved booking without an approver was approved automatically.
  getPersonColumn<ReservationDashboardRow>(approvedByColumnSettings, {
    getDisplay: (row) => row.approvedByDisplay,
    getLeadingDetail: (row) =>
      row.approvedAt ? formatReservationTimestamp(row.approvedAt, timezone) : null,
    getPerson: (row) =>
      row.approvedByUser
        ? {
            avatar: row.approvedByAvatar,
            email: row.approvedByEmail,
            firstName: row.approvedByUser.firstName,
            kind: 'staff',
            lastName: row.approvedByUser.lastName,
            role: row.approvedByRole,
          }
        : null,
    id: 'approvedBy',
    pageName: t('reservations:dashboard.pageName'),
    renderFallback: (row) =>
      row.approved ? (
        <Typography color="black800" size="text-small" weight="regular">
          {t('reservations:dashboard.approvedBy.auto')}
        </Typography>
      ) : (
        EMPTY_VALUE
      ),
    sortKey: 'approvedBy',
    source: 'reservations',
    title: t('reservations:dashboard.columns.approvedBy'),
  }),
  {
    id: 'status',
    render: (row) => (
      <Badge color={STATUS_COLOR_MAP[row.status]} size="small" variant="light">
        {getStatusLabel(t, row.status)}
      </Badge>
    ),
    title: t('reservations:dashboard.columns.status'),
    width: '11rem',
  },
  {
    excludeFromVisibilityControl: true,
    id: 'actions',
    render: (row) => {
      if (row.status !== ReservationStatusFilterEnum.enum.pending) return null
      return (
        <ApproveDeclineButtons
          approveLoading={approvingIds.has(row.id)}
          declineDisabled={approvingIds.has(row.id)}
          onApprove={() => onApprove(row)}
          onDecline={() => onDecline(row)}
        />
      )
    },
    title: t('reservations:dashboard.columns.actions'),
    width: '10rem',
  },
]
